import { ClockCountdown } from '@liveauctioneers/hammer-ui-core/icons';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect, useState } from 'react';

import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogEndTime,
    getCatalogIsCatalogOnly,
    getCatalogIsDone,
    getCatalogIsTimedPlus,
    getCatalogIsTimedPlusBiddingOpen,
    getCatalogIsTimedPlusClosing,
    getCatalogStartTime,
    getCatalogStatus,
    getCatalogTimedPlusBiddingEndsFromTs,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { longFormatDate } from '@/utils/dates';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { shouldShowLive } from '@/utils/live';

type Props = {
    catalogId: number;
    className?: string;
    forceIsLive?: boolean;
    forceOneDate?: boolean;
    hideBorders?: boolean;
    hideIcon?: boolean;
};

export const CatalogDate = ({ catalogId, className, forceIsLive, forceOneDate, hideBorders, hideIcon }: Props) => {
    const isCatalogDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const isCatalogTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const biddingEndsFrom =
        useAppSelector((state) => getCatalogTimedPlusBiddingEndsFromTs(state, catalogId)) || Date.now() / 1000;
    const biddingOpens = useAppSelector((state) => getCatalogStartTime(state, catalogId)) || Date.now() / 1000;
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const saleEndTs = useAppSelector((state) => getCatalogEndTime(state, catalogId));
    const timedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));
    const timedPlusClosing = useAppSelector((state) => getCatalogIsTimedPlusClosing(state, catalogId));
    const isLive = forceIsLive || shouldShowLive(catalogStatus, isCatalogOnly, biddingOpens);
    const showTwoDates = !forceOneDate && !timedPlusBiddingOpen;
    const formatMessage = useAppFormatMessage();

    const [biddingOpensFromDate, setBiddingOpensFromDate] = useState('');
    const [saleEndDate, setSaleEndDate] = useState('');
    const [biddingEndsFromDate, setBiddingEndsFromDate] = useState('');

    useEffect(() => {
        setBiddingOpensFromDate(longFormatDate(biddingOpens));
        setSaleEndDate(longFormatDate(saleEndTs));
        setBiddingEndsFromDate(longFormatDate(biddingEndsFrom));
    }, [biddingEndsFrom, biddingOpens, saleEndTs]);

    return (
        <div
            className={cn(
                'flex flex-row justify-start border-r border-border-secondary pr-md xsMax:mb-xs xsMax:border-r-0',
                showTwoDates ? 'items-start' : 'items-center',
                'xsMax:items-center',
                hideBorders && 'border-none',
                className
            )}
            data-testid="catalog-date"
        >
            {isCatalogDone ? (
                <>
                    {isCatalogTimedPlus && !hideIcon && (
                        <div className="mr-xs flex flex-row items-center justify-center xsMax:mr-2xs [&>div]:h-5">
                            <ClockCountdown
                                color="primary"
                                data-testid="timed-plus-icon"
                                id="catalog-date-timed-plus-icon-trigger"
                                size="sm"
                            />
                            <Tooltip anchorSelect="#catalog-date-timed-plus-icon-trigger">
                                {formatMessage(['catalogPage', 'catalogDate', 'timedPlusMessage'], {
                                    values: {
                                        link: (
                                            <Link
                                                key="catalog-date-timed-plus-message-link"
                                                href="https://support.liveauctioneers.com/hc/en-gb/articles/24574071591313-How-do-Timed-auctions-work"
                                                rel="noopener"
                                                target="_blank"
                                            >
                                                {formatMessage(['catalogPage', 'catalogDate', 'learnMore'])}
                                            </Link>
                                        ),
                                    },
                                })}
                            </Tooltip>
                        </div>
                    )}
                    <BodySecondary
                        className="uppercase"
                        color="secondary"
                    >
                        {formatMessage(['catalogPage', 'catalogDate', 'done'])}
                        {Boolean(saleEndDate) && (
                            <BodySecondary
                                className="ml-sm inline-block border-l border-border-secondary pl-sm xsMax:ml-xs xsMax:pl-xs"
                                color="secondary"
                                data-testid="end-time"
                            >
                                {saleEndDate}
                            </BodySecondary>
                        )}
                    </BodySecondary>
                </>
            ) : isCatalogTimedPlus ? (
                <>
                    {!hideIcon && (
                        <div className="mr-xs flex flex-row items-center justify-center xsMax:mr-2xs [&>div]:h-5">
                            <ClockCountdown
                                color="primary"
                                data-testid="timed-plus-icon"
                                id="catalog-date-timed-plus-icon-trigger"
                                size="sm"
                            />
                            <Tooltip anchorSelect="#catalog-date-timed-plus-icon-trigger">
                                {formatMessage(['catalogPage', 'catalogDate', 'timedPlusMessage'], {
                                    values: {
                                        link: (
                                            <Link
                                                key="catalog-date-timed-plus-message-link"
                                                href="https://support.liveauctioneers.com/hc/en-gb/articles/24574071591313-How-do-Timed-auctions-work"
                                                rel="noopener"
                                                target="_blank"
                                            >
                                                {formatMessage(['catalogPage', 'catalogDate', 'learnMore'])}
                                            </Link>
                                        ),
                                    },
                                })}
                            </Tooltip>
                        </div>
                    )}
                    <div className="flex flex-col items-start justify-center xsMax:ml-2xs xsMax:border-l xsMax:border-border-secondary xsMax:pl-xs">
                        {showTwoDates && (
                            <BodySecondary
                                className="mb-2xs block"
                                color="secondary"
                            >
                                {formatMessage(['catalogPage', 'catalogDate', 'startsOn'], {
                                    values: { date: biddingOpensFromDate },
                                })}
                            </BodySecondary>
                        )}
                        {timedPlusClosing ? (
                            <BodySecondary
                                className="block"
                                color="secondary"
                            >
                                {formatMessage(['catalogPage', 'catalogDate', 'endingNow'])}
                            </BodySecondary>
                        ) : (
                            <BodySecondary
                                className="block"
                                color="secondary"
                            >
                                {formatMessage(['catalogPage', 'catalogDate', 'endsFrom'], {
                                    values: { date: biddingEndsFromDate },
                                })}
                            </BodySecondary>
                        )}
                    </div>
                </>
            ) : isLive ? (
                <BodySecondary
                    className="block"
                    color="secondary"
                    data-testid="timed-or-live-date"
                >
                    {formatMessage(['catalogPage', 'catalogDate', 'started'], {
                        values: { date: biddingOpensFromDate },
                    })}
                </BodySecondary>
            ) : (
                <BodySecondary
                    className="block"
                    color="secondary"
                    data-testid="timed-or-live-date"
                >
                    {formatMessage(['catalogPage', 'catalogDate', 'startsOn'], {
                        values: { date: biddingOpensFromDate },
                    })}
                </BodySecondary>
            )}
        </div>
    );
};
